if (!customElements.get('pickup-warehouse-locator')) {
  customElements.define(
    'pickup-warehouse-locator',
    class PickupWarehouseLocator extends HTMLElement {
      static STORAGE_KEY = 'hypertheme:pickup-user-location';

      connectedCallback() {
        // BasicModal moves the drawer to <body> on open and back on close, so
        // connectedCallback fires multiple times — initialize once.
        if (this._initialized) return;
        this._initialized = true;

        this._apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
        this._country = this.dataset.country || '';
        this._fetchedOnce = false;
        this._abortController = null;

        try {
          this._i18n = JSON.parse(this.querySelector('[data-locator-i18n]').textContent);
        } catch (e) {
          this._i18n = {};
        }

        this._summary = this.querySelector('[data-locator-summary]');
        this._label = this.querySelector('[data-locator-label]');
        this._form = this.querySelector('[data-locator-form]');
        this._input = this._form.querySelector('input[name="address"]');
        this._status = this.querySelector('[data-locator-status]');
        this._editBtn = this.querySelector('[data-locator-edit]');
        this._geoBtn = this.querySelector('[data-locator-geo]');

        this._drawer = this.closest('basic-modal');
        const body = this.closest('.drawer__body');
        this._list = body ? body.querySelector('.pickup-availability-list') : null;
        if (this._list) {
          Array.from(this._list.children).forEach((li, i) => {
            li.dataset.originalIndex = String(i);
          });
        }

        this._form.addEventListener('submit', (e) => {
          e.preventDefault();
          const value = this._input.value.trim();
          if (value) this._lookup({ type: 'address', address: value, label: value });
        });
        this._geoBtn.addEventListener('click', () => this._useGeolocation());
        this._editBtn.addEventListener('click', () => {
          const show = this._form.hasAttribute('hidden');
          this._toggleForm(show);
          if (show) this._input.focus();
        });

        const saved = this._getSaved();
        if (saved && saved.label) {
          this._showSummary(saved.label);
          this._toggleForm(false);
        } else {
          this._toggleForm(true);
        }

        // Lazy fetch: only when the drawer is first opened with a saved location.
        if (this._drawer) {
          this._drawer.addEventListener('toggle', () => {
            if (!this._drawer.hasAttribute('open') || this._fetchedOnce) return;
            const stored = this._getSaved();
            if (stored && (stored.address || (stored.lat != null && stored.lng != null))) {
              this._lookup(stored);
            }
          });
        }
      }

      /* ------------------------------ storage ------------------------------ */

      _getSaved() {
        try {
          const utils = window.FoxTheme && window.FoxTheme.utils;
          if (utils && typeof utils.getStorage === 'function') {
            return utils.getStorage(PickupWarehouseLocator.STORAGE_KEY);
          }
          return JSON.parse(localStorage.getItem(PickupWarehouseLocator.STORAGE_KEY));
        } catch (e) {
          return null;
        }
      }

      _save(value) {
        try {
          const utils = window.FoxTheme && window.FoxTheme.utils;
          if (utils && typeof utils.setStorage === 'function') {
            utils.setStorage(PickupWarehouseLocator.STORAGE_KEY, value, 30);
          } else {
            localStorage.setItem(PickupWarehouseLocator.STORAGE_KEY, JSON.stringify(value));
          }
        } catch (e) {
          /* storage unavailable — feature still works within the page */
        }
      }

      /* ----------------------------- geolocation --------------------------- */

      _useGeolocation() {
        if (!navigator.geolocation) {
          this._setStatus(this._i18n.geolocationUnavailable, true);
          this._input.focus();
          return;
        }
        this._setBusy(true);
        this._setStatus(this._i18n.locating);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this._lookup({
              type: 'geo',
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              label: this._i18n.yourLocation,
            });
          },
          (err) => {
            this._setBusy(false);
            const denied = err && err.code === 1;
            this._setStatus(denied ? this._i18n.geolocationDenied : this._i18n.geolocationUnavailable, true);
            this._input.focus();
          },
          { timeout: 10000, maximumAge: 300000 }
        );
      }

      /* -------------------------------- fetch ------------------------------ */

      _lookup(params) {
        if (!this._apiBase) return;

        // Prefer coordinates when available (saved lookups already carry the
        // geocoded origin) — skips a Geocoding call on the backend.
        const query = new URLSearchParams();
        if (params.lat != null && params.lng != null) {
          query.set('lat', String(params.lat));
          query.set('lng', String(params.lng));
        } else if (params.address) {
          query.set('address', params.address);
          if (this._country) query.set('country', this._country);
        } else {
          return;
        }

        if (this._abortController) this._abortController.abort();
        this._abortController = new AbortController();

        this._setBusy(true);
        this._setStatus(this._i18n.loadingDistances);

        fetch(`${this._apiBase}/api/storefront/pickup-distances?${query}`, {
          signal: this._abortController.signal,
        })
          .then(async (res) => {
            if (!res.ok) {
              let code = null;
              try {
                code = (await res.json()).error;
              } catch (e) {
                /* non-JSON error body */
              }
              throw new Error(code || `HTTP_${res.status}`);
            }
            return res.json();
          })
          .then((data) => this._apply(data, params))
          .catch((err) => {
            if (err.name === 'AbortError') return;
            this._setBusy(false);
            const notFound = err.message === 'ADDRESS_NOT_FOUND';
            this._setStatus(notFound ? this._i18n.addressNotFound : this._i18n.distanceError, true);
            if (notFound) this._input.focus();
          });
      }

      _apply(data, params) {
        this._fetchedOnce = true;
        this._setBusy(false);
        this._setStatus('');

        const label = params.label || data.origin.formattedAddress || this._i18n.yourLocation;
        this._showSummary(label);
        this._toggleForm(false);

        const byId = new Map();
        data.locations.forEach((loc, order) => {
          byId.set(String(loc.id).split('/').pop(), Object.assign({ order: order }, loc));
        });

        let nearest = data.locations.find((l) => l.routeAvailable) || data.locations[0] || null;

        if (this._list) {
          const items = Array.from(this._list.children);
          items.forEach((li) => {
            const match =
              byId.get(String(li.dataset.locationId || '').split('/').pop()) ||
              data.locations.find(
                (l) => l.name.trim().toLowerCase() === (li.dataset.locationName || '').trim().toLowerCase()
              );
            this._renderDistance(li, match);
            li.dataset.sortOrder = match ? String(byId.get(String(match.id).split('/').pop()).order) : '';
          });

          items.sort((a, b) => {
            const oa = a.dataset.sortOrder === '' ? Infinity : Number(a.dataset.sortOrder);
            const ob = b.dataset.sortOrder === '' ? Infinity : Number(b.dataset.sortOrder);
            if (oa !== ob) return oa - ob;
            return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
          });
          this._list.append(...items);
        }

        if (nearest) this._updatePickupBar(nearest);

        this._save({
          type: params.type || 'address',
          lat: data.origin.lat,
          lng: data.origin.lng,
          address: params.address || null,
          label: label,
        });
      }

      _renderDistance(li, loc) {
        const line = li.querySelector('[data-distance-line]');
        if (!line) return;
        line.classList.remove('pickup-availability-card__distance--too-far');
        if (loc && loc.routeAvailable && loc.durationText && loc.distanceText) {
          line.textContent = (this._i18n.driveTimeDistance || '{{ duration }} drive • {{ distance }}')
            .replace('{{ duration }}', loc.durationText)
            .replace('{{ distance }}', loc.distanceText);
          line.removeAttribute('hidden');
        } else if (loc && loc.tooFar) {
          line.textContent = this._i18n.tooFar || '';
          line.classList.add('pickup-availability-card__distance--too-far');
          line.removeAttribute('hidden');
        } else {
          line.textContent = '';
          line.setAttribute('hidden', '');
        }
      }

      _updatePickupBar(loc) {
        const a = loc.address || {};
        const full = [a.address1, [a.city, a.province].filter(Boolean).join(', '), a.zip, a.country]
          .filter(Boolean)
          .join(', ');
        const query = [loc.name, a.address1, a.city, a.province, a.zip].filter(Boolean).join(' ');
        document.querySelectorAll('[data-pickup-bar]').forEach((bar) => {
          // A warehouse the user picked via "Pickup here" (fulfillment-selector
          // stamps data-user-selected on that bar) wins over the
          // nearest-warehouse auto-update — but only for that bar; other
          // products' bars on the page still follow the nearest warehouse.
          if (bar.hasAttribute('data-user-selected')) return;
          const nameEl = bar.querySelector('[data-pickup-bar-name]');
          if (nameEl) nameEl.textContent = loc.name;
          if (full) {
            const addressEl = bar.querySelector('[data-pickup-bar-address]');
            if (addressEl) addressEl.textContent = full;
          }
          if (query) {
            const linkEl = bar.querySelector('[data-pickup-bar-link]');
            if (linkEl) linkEl.href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query);
          }
        });
      }

      /* ------------------------------- ui bits ------------------------------ */

      _showSummary(label) {
        this._label.textContent = label;
        this._summary.removeAttribute('hidden');
      }

      _toggleForm(show) {
        if (show) {
          this._form.removeAttribute('hidden');
        } else {
          this._form.setAttribute('hidden', '');
        }
        this._editBtn.setAttribute('aria-expanded', show ? 'true' : 'false');
      }

      _setStatus(message, isError) {
        if (!message) {
          this._status.textContent = '';
          this._status.setAttribute('hidden', '');
          return;
        }
        this._status.textContent = message;
        this._status.classList.toggle('pickup-locator__status--error', !!isError);
        this._status.removeAttribute('hidden');
      }

      _setBusy(busy) {
        this.toggleAttribute('aria-busy', busy);
        [this._geoBtn, this._form.querySelector('[data-locator-submit]')].forEach((btn) => {
          if (btn) btn.disabled = busy;
        });
      }
    }
  );
}
